import React, { useState, useEffect } from 'react'
import AppLayout from '../components/AppLayout'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'

const DAYS = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday']
const emptyDay = (day) => ({ day, date: '', time_in: '', time_out: '', break_mins: '', hours: '', tasks: '' })

function calcHours(timeIn, timeOut, breakMins) {
  if (!timeIn || !timeOut) return ''
  const [ih, im] = timeIn.split(':').map(Number)
  const [oh, om] = timeOut.split(':').map(Number)
  let mins = (oh * 60 + om) - (ih * 60 + im)
  mins = Math.max(0, mins - (parseInt(breakMins) || 0))
  return (mins / 60).toFixed(2)
}

export default function Timesheets() {
  const { user, profile, isAdminOrManager, canEditData } = useAuth()
  const [sheets, setSheets] = useState([])
  const [tab, setTab] = useState('new')
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState('')
  const [staff, setStaff] = useState([])

  const [form, setForm] = useState({
    worker_id: user?.id || '',
    worker_name: profile?.full_name || '',
    role: '',
    pay_rate: '',
    week_ending: '',
    days: DAYS.map(emptyDay)
  })

  useEffect(() => {
    if (isAdminOrManager) {
      // Load both digital staff and manual workers
      Promise.all([
        supabase.from('profiles').select('id, full_name, permission_level'),
        supabase.from('manual_workers').select('id, full_name, department, role').eq('is_active', true)
      ]).then(([profileRes, manualRes]) => {
        const digital = (profileRes.data || []).map(p => ({ ...p, source: 'digital' }))
        const manual = (manualRes.data || []).map(m => ({ ...m, permission_level: 'staff', source: 'manual', id: 'manual_' + m.id }))
        setStaff([...digital, ...manual])
      })
    }
    if (tab === 'history') loadSheets()
  }, [tab, isAdminOrManager])

  const loadSheets = async () => {
    setLoading(true)
    let query = supabase.from('timesheets').select('*, profiles!worker_id(full_name), approver:profiles!approved_by(full_name)').order('week_ending', { ascending: false }).limit(30)
    if (!isAdminOrManager) query = query.eq('worker_id', user.id)
    const { data } = await query
    setSheets(data || [])
    setLoading(false)
  }

  const setDay = (i, field, val) => {
    const days = [...form.days]
    days[i] = { ...days[i], [field]: val }
    if (field === 'time_in' || field === 'time_out' || field === 'break_mins') {
      days[i].hours = calcHours(
        field === 'time_in' ? val : days[i].time_in,
        field === 'time_out' ? val : days[i].time_out,
        field === 'break_mins' ? val : days[i].break_mins
      )
    }
    setForm(f => ({ ...f, days }))
  }

  const totalHours = form.days.reduce((s, d) => s + (parseFloat(d.hours)||0), 0)
  const totalPay = totalHours * (parseFloat(form.pay_rate)||0)

  const handleSave = async () => {
    if (!form.week_ending) { setError('Please set the week ending date.'); return }
    setSaving(true); setError(''); setSaved(false)
    const { error: err } = await supabase.from('timesheets').insert({
      worker_id: form.worker_id || user.id,
      week_ending: form.week_ending,
      role: form.role,
      pay_rate: parseFloat(form.pay_rate)||0,
      days: form.days.filter(d => d.time_in || d.hours),
      total_hours: totalHours,
      total_pay: totalPay,
      is_approved: false
    })
    if (err) { setError(err.message); setSaving(false); return }
    setSaved(true); setSaving(false)
    setTimeout(() => setSaved(false), 3000)
    setForm(f => ({ ...f, week_ending: '', days: DAYS.map(emptyDay) }))
    setTab('history'); loadSheets()
  }

  const handleApprove = async (id) => {
    await supabase.from('timesheets').update({ is_approved: true, approved_by: user.id }).eq('id', id)
    loadSheets()
  }

  return (
    <AppLayout title="Timesheets">
      <div className="section-header">
        <div className="section-icon">⏱️</div>
        <div className="section-info">
          <h2>Timesheets</h2>
          <p>Weekly timesheets for all staff. Hours auto-calculated. Admins and managers approve before payment.</p>
        </div>
      </div>

      <div className="tabs">
        <button className={`tab ${tab==='new'?'active':''}`} onClick={() => setTab('new')}>📝 Submit Timesheet</button>
        <button className={`tab ${tab==='history'?'active':''}`} onClick={() => setTab('history')}>📚 {isAdminOrManager ? 'All Timesheets' : 'My Timesheets'}</button>
      </div>

      {tab === 'new' && (
        <div className="card">
          {saved && <div className="alert alert-green">✅ Timesheet submitted!</div>}
          {error && <div className="alert alert-red">❌ {error}</div>}

          <div className="grid-2" style={{ marginBottom: 14 }}>
            {isAdminOrManager ? (
              <div className="form-group">
                <label className="form-label">Worker</label>
                <select className="form-select" value={form.worker_id} onChange={e => {
                  const s = staff.find(p => p.id === e.target.value)
                  setForm(f => ({ ...f, worker_id: e.target.value, worker_name: s?.full_name || '' }))
                }}>
                  <option value={user.id}>{profile?.full_name} (me)</option>
                  {staff.filter(s => s.id !== user.id).map(s => <option key={s.id} value={s.id}>{s.full_name}</option>)}
                </select>
              </div>
            ) : (
              <div className="form-group"><label className="form-label">Worker</label><input className="form-input" value={profile?.full_name || ''} readOnly style={{ background: '#f0f0e8' }} /></div>
            )}
            <div className="form-group"><label className="form-label">Week Ending</label><input className="form-input" type="date" value={form.week_ending} onChange={e => setForm(f=>({...f,week_ending:e.target.value}))} /></div>
            <div className="form-group"><label className="form-label">Role / Position</label><input className="form-input" placeholder="e.g. Farm Assistant" value={form.role} onChange={e => setForm(f=>({...f,role:e.target.value}))} /></div>
            <div className="form-group"><label className="form-label">Pay Rate (UGX/hr)</label><input className="form-input" type="number" placeholder="0" value={form.pay_rate} onChange={e => setForm(f=>({...f,pay_rate:e.target.value}))} /></div>
          </div>

          <div className="table-wrap" style={{ marginBottom: 12 }}>
            <table>
              <thead><tr><th>Day</th><th>Date</th><th>Time In</th><th>Time Out</th><th>Break (min)</th><th>Net Hours</th><th>Tasks</th></tr></thead>
              <tbody>
                {form.days.map((day, i) => (
                  <tr key={day.day}>
                    <td style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{day.day}</td>
                    <td><input className="form-input" type="date" style={{ width: 135 }} value={day.date} onChange={e => setDay(i,'date',e.target.value)} /></td>
                    <td><input className="form-input" type="time" style={{ width: 95 }} value={day.time_in} onChange={e => setDay(i,'time_in',e.target.value)} /></td>
                    <td><input className="form-input" type="time" style={{ width: 95 }} value={day.time_out} onChange={e => setDay(i,'time_out',e.target.value)} /></td>
                    <td><input className="form-input" type="number" style={{ width: 70 }} placeholder="0" value={day.break_mins} onChange={e => setDay(i,'break_mins',e.target.value)} /></td>
                    <td><input className="form-input" style={{ width: 75, fontWeight: 700, background: '#f8f8f0' }} value={day.hours || ''} readOnly /></td>
                    <td><input className="form-input" placeholder="Tasks done today" value={day.tasks} onChange={e => setDay(i,'tasks',e.target.value)} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ background: 'var(--green-pale)', borderRadius: 10, padding: 16, display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 16 }}>
            {[['Total Hours', totalHours.toFixed(2)+' hrs', 'var(--green-mid)'], ['Pay Rate', 'UGX '+(parseFloat(form.pay_rate)||0).toLocaleString()+'/hr', 'var(--text)'], ['Total Pay', 'UGX '+totalPay.toLocaleString(), 'var(--amber)']].map(([label,val,color]) => (
              <div key={label} style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '0.65rem', fontFamily: "'DM Mono',monospace", textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-muted)', marginBottom: 4 }}>{label}</div>
                <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '1.3rem', fontWeight: 700, color }}>{val}</div>
              </div>
            ))}
          </div>

          <button className="btn btn-primary btn-lg" onClick={handleSave} disabled={saving}>{saving ? <><span className="spinner" /> Saving...</> : '💾 Submit Timesheet'}</button>
        </div>
      )}

      {tab === 'history' && (
        <div className="card">
          <div className="card-title">{isAdminOrManager ? 'All Timesheets' : 'My Timesheets'}</div>
          {loading ? <div style={{ textAlign: 'center', padding: 32 }}><span className="spinner dark" /></div>
          : sheets.length === 0 ? <div className="empty-state"><div className="empty-icon">⏱️</div><h3>No timesheets yet</h3></div>
          : (
            <div className="table-wrap">
              <table>
                <thead><tr><th>Worker</th><th>Week Ending</th><th>Hours</th><th>Pay (UGX)</th><th>Status</th>{isAdminOrManager && <th>Action</th>}</tr></thead>
                <tbody>
                  {sheets.map(s => (
                    <tr key={s.id}>
                      <td style={{ fontWeight: 600 }}>{s.profiles?.full_name || '—'}</td>
                      <td>{new Date(s.week_ending).toLocaleDateString('en-UG')}</td>
                      <td style={{ fontWeight: 600 }}>{s.total_hours} hrs</td>
                      <td style={{ color: 'var(--amber)', fontWeight: 600 }}>{(s.total_pay||0).toLocaleString()}</td>
                      <td>
                        {s.is_approved
                          ? <span className="badge badge-green">✅ Approved</span>
                          : <span className="badge badge-amber">⏳ Pending</span>}
                      </td>
                      {isAdminOrManager && (
                        <td>{!s.is_approved && <button className="btn btn-secondary btn-sm" onClick={() => handleApprove(s.id)}>Approve</button>}</td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </AppLayout>
  )
}
